/**
 * Smooths out mouse-wheel scrolling.
 *
 * A traditional notched mouse wheel reports scroll input as whole "lines"
 * (WheelEvent.deltaMode === 1), and by default the browser jumps the page by
 * that amount instantly for every notch - which feels jerky compared to a
 * trackpad, which reports fine-grained pixel deltas (deltaMode === 0) that
 * the browser already renders smoothly on its own.
 *
 * This intercepts only the "line mode" wheel events and eases them into a
 * smooth glide, one requestAnimationFrame step at a time, instead of an
 * instant jump. Trackpad and precise-mouse input (deltaMode === 0) is left
 * completely untouched, since it is already smooth.
 *
 * It also steps aside for:
 * - prefers-reduced-motion
 * - pinch-zoom gestures (ctrlKey) and shift+wheel horizontal scroll
 * - any scroll happening inside a nested scrollable widget (cart drawer,
 *   modals, horizontal carousels, etc.) so those keep their native behavior
 */
import { getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

const PIXELS_PER_NOTCH = 120; // approx. distance the browser scrolls per wheel click
const EASE_FACTOR = 0.18; // higher = snappier, lower = floatier
const SETTLE_THRESHOLD = 0.5; // px

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!prefersReducedMotion) {
  let targetScrollTop = null;
  let rafId = null;

  /**
   * Walks up from the event target to see if it's inside a scrollable
   * element other than the page's own scroll container (e.g. a drawer,
   * modal, or carousel), in which case native scrolling should handle it.
   *
   * @param {EventTarget | null} eventTarget
   * @param {Element} mainContainer
   * @returns {boolean}
   */
  function isInsideNestedScrollable(eventTarget, mainContainer) {
    let node = eventTarget instanceof Element ? eventTarget : null;

    while (node && node !== document.documentElement && node !== mainContainer) {
      const style = getComputedStyle(node);
      const scrollableY = /(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight;

      if (scrollableY) return true;

      node = node.parentElement;
    }

    return false;
  }

  function animateStep() {
    const container = getScrollContainer();
    const current = container.scrollTop;
    const diff = targetScrollTop - current;

    if (Math.abs(diff) < SETTLE_THRESHOLD) {
      container.scrollTop = targetScrollTop;
      rafId = null;
      return;
    }

    container.scrollTop = current + diff * EASE_FACTOR;
    rafId = requestAnimationFrame(animateStep);
  }

  window.addEventListener(
    'wheel',
    (event) => {
      // Trackpads and precise mice already report pixel deltas that scroll
      // smoothly natively - only line-mode (notched wheel) input needs help.
      if (event.deltaMode !== 1) return;

      // Don't hijack pinch-zoom or shift+wheel horizontal scroll.
      if (event.ctrlKey || event.metaKey || event.shiftKey) return;

      const container = getScrollContainer();

      if (isInsideNestedScrollable(event.target, container)) return;

      const maxScrollTop = container.scrollHeight - container.clientHeight;
      if (maxScrollTop <= 0) return;

      event.preventDefault();

      const current = container.scrollTop;
      const base = rafId === null || targetScrollTop === null ? current : targetScrollTop;
      const direction = event.deltaY > 0 ? 1 : -1;

      targetScrollTop = Math.min(Math.max(base + direction * PIXELS_PER_NOTCH, 0), maxScrollTop);

      if (rafId === null) {
        rafId = requestAnimationFrame(animateStep);
      }
    },
    { passive: false }
  );

  // If the scroll container changes (mobile <-> desktop squeeze layout),
  // drop any in-flight animation rather than let it target the wrong element.
  scrollContainerMediaQuery.addEventListener('change', () => {
    targetScrollTop = null;
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  });
}
